import { Injectable, OnDestroy } from "@angular/core";
import { NGXLogger } from "ngx-logger";

/**
 * Single source of truth for the `window.prerenderReady` lifecycle.
 *
 * Prerender.io watches `window.prerenderReady` during SSR to decide when a page
 * is ready to snapshot. This service owns the fallback ceiling timer:
 *
 *  - {@link armCeiling} is called on every route init; it re-seeds the flag to
 *    boolean `false` and arms a ceiling timer.
 *  - Real terminal signals (fetch success / 404 / 503) call {@link signalReady},
 *    which cancels the armed ceiling (so it can't later fire a spurious
 *    `prerender-ceiling-fired` log) and sets the flag to `true`.
 *  - If no real signal arrives within `CEILING_MS`, the ceiling callback fires,
 *    logs `prerender-ceiling-fired` (only when the flag is still `false`, so the
 *    event is log-distinct from a real signal), and sets the flag to `true`.
 *
 * `prerenderReady` must always be boolean `false` / `true` — never `undefined` —
 * because Prerender.io falls back to its own early-capture heuristic when the
 * value is undefined.
 */
@Injectable()
export class PrerenderReadyService implements OnDestroy {
  /** Fallback ceiling delay (ms) after which the page is snapshotted anyway. */
  private readonly CEILING_MS: number = 2000;

  /** Handle of the armed ceiling timer; `null` when no timer is pending. */
  private timerId: ReturnType<typeof setTimeout> | null = null;

  /** `performance.now()` captured when a ceiling is armed, for elapsed-ms logs. */
  private armedAt: number = 0;

  constructor(private ngxLogger: NGXLogger) {}

  /**
   * Route init: re-seed `prerenderReady = false` and arm the ceiling timer.
   * Idempotent per navigation — clears any leftover armed timer first so it
   * can't leak across route changes.
   */
  armCeiling(url: string): void {
    this.clearTimer();
    this.setReady(false);
    this.armedAt = performance.now();
    this.timerId = setTimeout(() => {
      if (!(window as any).prerenderReady) {
        const elapsedMs: number = Math.round(performance.now() - this.armedAt);
        this.ngxLogger.warn("prerender-ceiling-fired", { url, elapsedMs });
        this.setReady(true);
      }
    }, this.CEILING_MS);
  }

  /**
   * Real-signal entry point (fetch success / 404 / 503). Cancels the armed
   * ceiling so it can't later log a spurious `prerender-ceiling-fired`, then
   * sets the flag to `true`.
   */
  signalReady(): void {
    this.clearTimer();

    if (!(window as any).prerenderReady) {
      this.setReady(true);
    }
  }

  /** Cancels the armed ceiling timer if one is pending. */
  private clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  public beginPrerender() {
    this.setReady(false);
  }

  private setReady(value: boolean): void {
    (window as any).prerenderReady = value;
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }
}
